/**
 * What the trigger form offers, and what it can be handed.
 *
 * The shared catalog in `shared/domain/triggers` mirrors the backend and is
 * parity-tested against it, so it keeps every entity and every change. Which
 * of them this form puts in front of a person is a policy of the form, and it
 * lives here. The UI descriptions answer with a tag rather than with words, so
 * the component that renders one owns the copy.
 */

import { type InvocationInput } from "@/shared/domain/actions";
import {
  actionTakesTheFile,
  entityOfSubscription,
  isTriggeringSession,
  TriggerSourceType,
  TriggerTargetType,
  type PlatformEntity,
  type TriggerV2Record,
} from "@/shared/domain/triggers";

import { type TriggerFormValues } from "./triggerFormSchema";

/** The entities the form watches, a subset of the catalog's. */
export const TRIGGER_FORM_ENTITIES = [
  "file",
  "dataset",
  "session",
  "invocation",
] as const satisfies readonly PlatformEntity[];

export type TriggerFormEntity = (typeof TRIGGER_FORM_ENTITIES)[number];

/** The kinds of target the picker offers, in the order it shows them. */
const TARGET_KINDS: readonly TriggerTargetType[] = [
  TriggerTargetType.InvokeAction,
  TriggerTargetType.StartAgent,
  TriggerTargetType.SendSlackMessage,
];

/**
 * Whether the input selects topics, or sessions other than the triggering one
 * a session trigger always passes (see `TRIGGERING_SESSION_SELECTOR`).
 */
function selectsAnythingButFiles(
  input: InvocationInput | null | undefined,
  passesTheSession: boolean,
): boolean {
  if (input === null || input === undefined) {
    return false;
  }
  const sessions =
    passesTheSession && isTriggeringSession(input.sessions)
      ? null
      : input.sessions;
  return chosen(input.topics) || chosen(sessions);
}

function chosen(selector: unknown): boolean {
  return Array.isArray(selector)
    ? selector.length > 0
    : selector !== null && selector !== undefined;
}

/**
 * Whether the edit route can open a stored trigger without losing anything it
 * holds. A trigger it turns away is still viewed, enabled, disabled, and
 * deleted from its details page; filling the form halfway and saving would
 * silently rewrite what the form has no controls for.
 */
export function isEditableInForm(record: TriggerV2Record): boolean {
  // The form's query rows select files only; a stored topics or sessions
  // selector has nowhere to go, except the triggering session.
  const passesTheSession =
    record.fires_on.type === TriggerSourceType.Events &&
    entityOfSubscription(record.fires_on.events) === "session";
  const selectsOnlyFiles = record.targets.every(
    (target) =>
      target.type !== TriggerTargetType.InvokeAction ||
      !selectsAnythingButFiles(target.invocation_input, passesTheSession),
  );
  if (!selectsOnlyFiles) {
    return false;
  }
  if (record.fires_on.type === TriggerSourceType.Schedule) {
    return true;
  }
  // Null for a subscription that mixes entities, which has no single one for
  // the entity Select; "event" for a subscription to annotation events, which
  // the form never shows.
  const entity = entityOfSubscription(record.fires_on.events);
  const watched = TRIGGER_FORM_ENTITIES.some((offered) => offered === entity);
  // Legal on the wire, but the form does not offer action chaining yet, so it
  // could not show this trigger as it stands.
  const chainsAnAction =
    entity === "invocation" &&
    record.targets.some(
      (target) => target.type === TriggerTargetType.InvokeAction,
    );
  return watched && !chainsAnAction;
}

/**
 * What the change itself supplies to an action, shown at the top of "Input
 * data". Null on a schedule, which fires with nothing, and on a change that
 * carries no files or session, such as an invocation's.
 */
export function suppliedInputFor(
  firesOn: TriggerFormValues["firesOn"],
): "changed-file" | "changed-dataset" | "changed-session" | null {
  if (firesOn.type === TriggerSourceType.Schedule) {
    return null;
  }
  const entity = entityOfSubscription(firesOn.events);
  if (entity === "session") {
    return "changed-session";
  }
  if (entity !== "file" && entity !== "dataset") {
    return null;
  }
  return actionTakesTheFile(firesOn.events, firesOn.oncePer)
    ? "changed-file"
    : "changed-dataset";
}

/** Which target kinds the picker offers, and why one of them is not offered. */
export function targetKindsFor(
  firesOn: TriggerFormValues["firesOn"],
): { kind: TriggerTargetType; unavailable: "action-chaining" | null }[] {
  const chaining =
    firesOn.type === TriggerSourceType.Events &&
    firesOn.entity === "invocation";
  return TARGET_KINDS.map((kind) => ({
    kind,
    unavailable:
      chaining && kind === TriggerTargetType.InvokeAction
        ? "action-chaining"
        : null,
  }));
}

/**
 * The kind a target starts on under `firesOn`: the first the picker actually
 * offers. Nothing is ever born on, or left sitting on, a kind the source has
 * closed, which is what keeps the picker's greyed-out option unselectable
 * rather than selected-and-invalid.
 */
export function defaultTargetKindFor(
  firesOn: TriggerFormValues["firesOn"],
): TriggerTargetType {
  return (
    targetKindsFor(firesOn).find((offered) => offered.unavailable === null)
      ?.kind ?? TriggerTargetType.StartAgent
  );
}

/** Why `firesOn` closes the kind `target` is on, or null if it does not. */
export function unavailableKindOf(
  firesOn: TriggerFormValues["firesOn"],
  type: TriggerTargetType,
): "action-chaining" | null {
  return (
    targetKindsFor(firesOn).find((offered) => offered.kind === type)
      ?.unavailable ?? null
  );
}

/**
 * Whether the source carries the fields of the kind its `type` names.
 *
 * The run-mode control writes only that tag, and the cascade replaces the rest
 * of the source in answer, so between the two writes a reader can see a
 * schedule with a subscription's fields under it. Nothing should be read off
 * one in that state.
 */
export function isSourceComplete(
  firesOn: TriggerFormValues["firesOn"],
): boolean {
  return firesOn.type === TriggerSourceType.Schedule
    ? typeof firesOn.time === "string" && typeof firesOn.cron === "string"
    : Array.isArray(firesOn.events);
}

/** Which submit the bar shows. */
export function submitKindFor(
  mode: "create" | "edit",
  enabled: boolean,
): "create" | "create-and-enable" | "save" {
  if (mode === "edit") {
    return "save";
  }
  return enabled ? "create-and-enable" : "create";
}
